// Copyright ©️ 2026 eVolve MEP, LLC

import type { WorkOrderItem } from '@evolvemep/api-ts';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { formatQuantity } from 'helpers/formatQuantity';
import { useIsMobile } from 'hooks/useIsMobile';
import { BadgeButton } from 'modules/Shop/WorkOrders/WorkOrderDetail/BadgeButton';

type Props = {
  item: Pick<WorkOrderItem, 'totalQuantity' | 'totalAllocatedQuantity' | 'totalShippedQuantity'>;
  onClick?: () => void;
  stopPropagation?: boolean;
};

/**
 * Indicates how much of a work order item has been allocated to shipping orders.
 */
export const ShipmentAllocationBadge = ({
  onClick,
  stopPropagation,
  item: { totalQuantity, totalAllocatedQuantity, totalShippedQuantity },
}: Props) => {
  const isMobile = useIsMobile();
  if (totalAllocatedQuantity <= 0 && totalShippedQuantity <= 0) {
    return null;
  }

  const hasStartedShipping = totalShippedQuantity > 0;

  return (
    <AgGridStyleTooltip
      label={`${formatQuantity(totalAllocatedQuantity)} of ${formatQuantity(totalQuantity)} added to shipping order(s)`}
      withArrow
      openOnTap
    >
      {isMobile ? (
        <EvolveIcon icon="Shipment" color={hasStartedShipping ? 'teal.8' : 'gray'} size="sm" />
      ) : (
        <BadgeButton
          color={hasStartedShipping ? 'teal.8' : 'gray'}
          variant="outline"
          size="xl"
          onClick={onClick}
          leftSection={<EvolveIcon icon="Shipment" />}
          stopPropagation={stopPropagation}
        >
          {totalAllocatedQuantity === totalQuantity
            ? formatQuantity(totalAllocatedQuantity)
            : `${formatQuantity(totalAllocatedQuantity)}/${formatQuantity(totalQuantity)}`}
        </BadgeButton>
      )}
    </AgGridStyleTooltip>
  );
};
